<template>
  <v-card flat>
    <v-card-text>
      <v-alert v-if="error" type="error" dismissible @input="error = null">
        <span v-text="error" />
      </v-alert>

      <v-alert v-if="successMessage" type="success" dismissible @input="successMessage = null">
        <span v-text="successMessage" />
      </v-alert>

      <v-radio-group v-model="mode" row :disabled="submitting">
        <v-radio label="Update Existing Parameter" value="update" />
        <v-radio label="Create New Parameter" value="create" />
      </v-radio-group>

      <v-form ref="form" v-model="valid">
        <v-row dense>
          <!-- Update Mode: Select from existing parameters -->
          <v-col v-if="mode === 'update'" cols="12">
            <v-autocomplete
              v-model="selectedParameter"
              :items="parameters"
              :loading="loadingParameters"
              :disabled="submitting"
              item-text="master_parameter.name"
              item-value="id"
              label="Select Parameter"
              hint="Choose a parameter to force update"
              persistent-hint
              return-object
              clearable
              @change="onParameterSelect"
            >
              <template #item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.master_parameter.name }}</v-list-item-title>
                  <v-list-item-subtitle>
                    Value: {{ item.value }} | Status: {{ item.status }} | Encoding:
                    {{ item.encoding }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>

          <!-- Create Mode: Select from master parameters -->
          <v-col v-if="mode === 'create'" cols="12">
            <v-autocomplete
              v-model="selectedMasterParameter"
              :items="masterParameters"
              :loading="loadingMasterParameters"
              :disabled="submitting"
              item-text="name"
              item-value="id"
              label="Select Master Parameter"
              hint="Choose a master parameter to create"
              persistent-hint
              return-object
              clearable
              @change="onMasterParameterSelect"
            >
              <template #item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>
                    Default: {{ item.default_value }} | Encoding: {{ item.encoding }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12">
            <v-text-field
              v-model="formData.value"
              :rules="valueRules"
              :disabled="!isParameterSelected || submitting"
              label="Value"
              hint="The parameter value"
              counter="255"
              required
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-select
              v-model="formData.status"
              :items="statuses"
              :rules="statusRules"
              :disabled="!isParameterSelected || submitting"
              label="Status"
              required
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-select
              v-model="formData.encoding"
              :items="encodings"
              :rules="encodingRules"
              :disabled="!isParameterSelected || submitting"
              label="Encoding"
              required
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-btn
              color="primary"
              :disabled="!isFormValid"
              :loading="submitting"
              @click="submitForm"
            >
              {{ mode === 'create' ? 'Force Create' : 'Force Update' }}
            </v-btn>
            <v-btn text :disabled="submitting" class="ml-2" @click="resetForm">
              Reset
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import HTTP from '@/api/dog';
import { enumFetch } from '@nielsen-media/ui-toolkit-vue-api';
import vrb from 'bsm-validation-rules';
import { types } from '@/helpers/validations';

export default {
  name: 'ForceUpdateParameterForm',
  props: {
    templateId: {
      type: Number,
      required: true,
      validator: value => value > 0,
    },
  },
  data() {
    return {
      valid: false,
      mode: 'update', // 'update' or 'create'
      parameters: [],
      selectedParameter: null,
      masterParameters: [],
      selectedMasterParameter: null,
      originalValues: {},
      formData: {
        value: '',
        status: '',
        encoding: '',
      },
      loadingParameters: false,
      loadingMasterParameters: false,
      submitting: false,
      statuses: [],
      encodings: [],
      error: null,
      successMessage: null,
      valueRules: [vrb.presence(), ...types.parameterValue],
      statusRules: [vrb.presence(), vrb.typeOf('string')],
      encodingRules: [vrb.presence(), vrb.typeOf('string')],
      abortController: null,
    };
  },
  computed: {
    isParameterSelected() {
      return this.mode === 'update' ? !!this.selectedParameter : !!this.selectedMasterParameter;
    },
    isFormValid() {
      if (!this.valid || !this.isParameterSelected || this.submitting) {
        return false;
      }

      // For update mode, require changes
      if (this.mode === 'update') {
        return this.hasChanges;
      }

      // For create mode, just need valid form
      return true;
    },
    hasChanges() {
      if (!this.selectedParameter) return false;

      return (
        this.formData.value !== this.originalValues.value ||
        this.formData.status !== this.originalValues.status ||
        this.formData.encoding !== this.originalValues.encoding
      );
    },
  },
  watch: {
    templateId: {
      immediate: true,
      handler() {
        this.fetchParameters();
      },
    },
    mode() {
      this.resetForm();
    },
  },
  created() {
    this.enumFetch(HTTP, {
      statuses: '/parameters/enum/statuses',
      encodings: '/parameters/enum/encodings',
    });
    this.fetchMasterParameters();
  },
  methods: {
    enumFetch,

    async fetchParameters() {
      if (!this.templateId) return;

      // Cancel any pending request
      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();
      this.loadingParameters = true;
      this.error = null;

      try {
        const response = await HTTP.get(`/applications/templates/${this.templateId}/parameters`, {
          signal: this.abortController.signal,
        });
        this.parameters = response.data;
      } catch (err) {
        if (err.name !== 'AbortError') {
          this.error = err.response?.data?.message || 'Failed to load parameters';
        }
      } finally {
        this.loadingParameters = false;
      }
    },

    async fetchMasterParameters() {
      this.loadingMasterParameters = true;
      this.error = null;

      try {
        const response = await HTTP.get('/master_parameters?per_page=300');
        this.masterParameters = response.data;
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to load master parameters';
      } finally {
        this.loadingMasterParameters = false;
      }
    },

    onParameterSelect(parameter) {
      if (!parameter) {
        this.resetFormFields();
        return;
      }

      // Pre-fill form with current parameter values
      this.formData.value = parameter.value || '';
      this.formData.status = parameter.status || '';
      this.formData.encoding = parameter.encoding || '';

      // Store original values for change detection
      this.originalValues = {
        value: parameter.value || '',
        status: parameter.status || '',
        encoding: parameter.encoding || '',
      };

      // Clear any previous messages
      this.error = null;
      this.successMessage = null;
    },

    onMasterParameterSelect(masterParameter) {
      if (!masterParameter) {
        this.resetFormFields();
        return;
      }

      // Pre-fill form with master parameter defaults
      this.formData.value = masterParameter.default_value || '';
      this.formData.status = 'active';
      this.formData.encoding = masterParameter.encoding || '';

      // Clear any previous messages
      this.error = null;
      this.successMessage = null;
    },

    getChangedFields() {
      const changes = {};

      if (this.formData.value !== this.originalValues.value) {
        changes.value = this.formData.value;
      }
      if (this.formData.status !== this.originalValues.status) {
        changes.status = this.formData.status;
      }
      if (this.formData.encoding !== this.originalValues.encoding) {
        changes.encoding = this.formData.encoding;
      }

      return changes;
    },

    async submitForm() {
      if (!this.isFormValid) return;

      if (this.mode === 'create') {
        await this.submitForceCreate();
      } else {
        await this.submitForceUpdate();
      }
    },

    async submitForceCreate() {
      this.submitting = true;
      this.error = null;
      this.successMessage = null;

      try {
        const payload = {
          master_parameter_id: this.selectedMasterParameter.id,
          value: this.formData.value,
          status: this.formData.status,
          encoding: this.formData.encoding,
        };

        const response = await HTTP.post(
          `/applications/templates/${this.templateId}/parameters/force_create`,
          payload,
        );

        this.successMessage = `Parameter ${this.selectedMasterParameter.name} created successfully!`;

        // Emit success event to parent
        this.$emit('success', {
          parameter: response.data,
          mode: 'create',
        });

        // Refresh parameters list
        await this.fetchParameters();

        // Reset form after successful creation (but keep success message)
        const successMsg = this.successMessage;
        this.resetForm();
        this.successMessage = successMsg;
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to create parameter';

        // Emit error event to parent
        this.$emit('error', {
          masterParameter: this.selectedMasterParameter,
          error: this.error,
        });
      } finally {
        this.submitting = false;
      }
    },

    async submitForceUpdate() {
      if (!this.isFormValid) return;

      this.submitting = true;
      this.error = null;
      this.successMessage = null;

      try {
        const changedFields = this.getChangedFields();

        await HTTP.patch(
          `/applications/templates/parameters/${this.selectedParameter.id}/force_update`,
          changedFields,
        );

        this.successMessage = `Parameter ${this.selectedParameter.master_parameter.name} force updated successfully!`;

        // Emit success event to parent
        this.$emit('success', {
          parameter: this.selectedParameter,
          changes: changedFields,
          mode: 'update',
        });

        // Refresh parameters list
        await this.fetchParameters();

        // Reset form after successful update (but keep success message)
        const successMsg = this.successMessage;
        this.resetForm();
        this.successMessage = successMsg;
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to force update parameter';

        // Emit error event to parent
        this.$emit('error', {
          parameter: this.selectedParameter,
          error: this.error,
        });
      } finally {
        this.submitting = false;
      }
    },

    resetFormFields() {
      this.formData = {
        value: '',
        status: '',
        encoding: '',
      };
      this.originalValues = {};
      this.error = null;
      this.successMessage = null;

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    resetForm() {
      this.selectedParameter = null;
      this.selectedMasterParameter = null;
      this.resetFormFields();
    },
  },
};
</script>

<style scoped>
.v-card {
  padding: 16px;
}
</style>
